import { useUser } from '@folklore/auth';
import { useTracking } from '@folklore/tracking';
import { useIsMutating, useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';

import { useApi } from '../contexts/ApiContext';
import { useIsCheckingAuth } from '../contexts/AuthContext';
import { usePageKey } from '../contexts/PageContext';
import { usePopupsContext } from '../contexts/PopupsContext';
import { useSubscriptions } from '../contexts/SiteContext';
import { updateAuthUser } from './useAuth';
import useCurrentUrl from './useCurrentUrl';

export function useSubscribe(defaultSubscription = null) {
    const api = useApi();
    const queryClient = useQueryClient();
    const url = useCurrentUrl();
    const { mutate, mutateAsync, ...mutationResult } = useMutation({
        mutationKey: ['subscribe', defaultSubscription],
        mutationFn: ({ subscription = defaultSubscription, ...data }) =>
            api.subscriptions.subscribe(subscription || defaultSubscription, {
                ...data,
                url,
            }),
        onSuccess: (response) => {
            queryClient.setQueryData(['auth'], response);
        },
    });

    return {
        subscribe: mutate,
        subscribeAsync: mutateAsync,
        ...mutationResult,
    };
}

export function useUnsubscribe(defaultSubscription = null) {
    const api = useApi();
    const queryClient = useQueryClient();
    const { mutate, mutateAsync, ...mutationResult } = useMutation({
        mutationKey: ['unsubscribe', defaultSubscription],
        mutationFn: (handle) => api.subscriptions.unsubscribe(handle || defaultSubscription),
        onSuccess: (newUser) => {
            updateAuthUser(queryClient, newUser);
        },
    });

    return {
        unsubscribe: mutate,
        unsubscribeAsync: mutateAsync,
        ...mutationResult,
    };
}

export function useSubscribed(subscription) {
    const user = useUser();
    const { subscriptions = [] } = user || {};
    return (
        (subscriptions || []).findIndex(
            ({ id, subscribed = false }) => id === subscription && subscribed,
        ) !== -1
    );
}

export function useSubscription(opts) {
    const {
        subscription: handle = null,
        source = null,
        onSubscribe = null,
        onUnsubscribe = null,
        withoutMessages = false,
        withoutEditContact = false,
        withoutLogin = false,
        friend = false,
    } = opts || {};
    const isCheckingAuth = useIsCheckingAuth();
    const allSubscriptions = useSubscriptions();
    const { addMessage } = usePopupsContext();

    const pageKey = usePageKey();
    const finalSource = [source, pageKey].filter((s) => s !== null).join(':');

    const subscription =
        allSubscriptions.find(({ handle: subscriptionHandle }) => subscriptionHandle === handle) ||
        null;
    const user = useUser() || {};
    const {
        subscriptions: userSubscriptions = [],
        phone = null,
        email = null,
    } = !withoutLogin && !friend ? user : {};
    const { subscribed = false, via: userVia } = userSubscriptions.find(
        ({ id }) => id === handle,
    ) || { subscribed: isCheckingAuth ? null : false };

    const { subscribeAsync } = useSubscribe(handle);
    const { unsubscribeAsync } = useUnsubscribe(handle);
    const isSubscribing = useIsMutating({
        mutationKey: ['subscribe', handle],
    });
    const isUnsubscribing = useIsMutating({
        mutationKey: ['unsubscribe', handle],
    });
    const [editContactNeeded, setEditContactNeeded] = useState(null);
    const editContactSubscribeData = useRef(null);
    const editContactResolve = useRef(null);

    const [subscriptionAfterAuth, setSubscriptionAfterAuth] = useState(null);
    const tracking = useTracking();
    const updateSubscription = (newSubscribed, data = null) => {
        if (isCheckingAuth) {
            return new Promise((resolve) => {
                setSubscriptionAfterAuth({
                    subscribed: newSubscribed,
                    data,
                    resolve,
                });
            });
        }
        const {
            subscription: dataSubscription = null,
            subscriptions: dataSubscriptions = [],
            phone: dataPhone = phone,
            email: dataEmail = email,
            via: dataVia = null,
        } = data || {};
        const subscriptions = allSubscriptions.filter(
            ({ handle: subscriptionHandle }) =>
                subscriptionHandle === handle ||
                subscriptionHandle === dataSubscription ||
                (dataSubscriptions || []).indexOf(subscriptionHandle) !== -1,
        );
        const viaMap = subscriptions.reduce((acc, { handle, via, group }) => {
            const key = group || handle;
            const currentVia = acc[key] || [];
            return {
                ...acc,
                [key]: [
                    ...currentVia,
                    ...(via || []).filter((viaType) => currentVia.indexOf(viaType) === -1),
                ],
            };
        }, {});
        const lonelyVia = Object.keys(viaMap)
            .map((key) => viaMap[key])
            .filter((via) => via.length === 1)
            .map((via) => via[0]);
        if (
            newSubscribed &&
            lonelyVia.indexOf('sms') !== -1 &&
            dataPhone === null &&
            !withoutEditContact
        ) {
            setEditContactNeeded('phone');
            return new Promise((resolve) => {
                editContactSubscribeData.current = data;
                editContactResolve.current = resolve;
            });
        }
        if (
            newSubscribed &&
            lonelyVia.indexOf('email') !== -1 &&
            dataEmail === null &&
            !withoutEditContact
        ) {
            setEditContactNeeded('email');
            return new Promise((resolve) => {
                editContactSubscribeData.current = data;
                editContactResolve.current = resolve;
            });
        }

        return newSubscribed
            ? subscribeAsync({
                  source: finalSource,
                  login: withoutLogin ? false : undefined,
                  friend: friend ? true : undefined,
                  ...data,
              }).then((response) => {
                  const defaultNewSubscriptions = subscriptions.map(({ handle }) => handle);
                  const {
                      user: newUser,
                      isNewSubscribed = true,
                      newSubscriptions = defaultNewSubscriptions,
                  } = response;
                  if (!withoutMessages) {
                      addMessage(isNewSubscribed ? 'subscribe' : 'already_subscribed', {
                          friend,
                          user: newUser,
                          subscription: subscriptions.length === 1 ? subscriptions[0] : null,
                      });
                  }
                  const subscriptionHandles = newSubscriptions || [];
                  if (subscriptionHandles.length > 0) {
                      tracking.trackSubscribe(subscriptionHandles, finalSource);
                  }
                  if (onSubscribe !== null) {
                      onSubscribe();
                  }
                  return response;
              })
            : unsubscribeAsync(data).then((newUser) => {
                  if (!withoutMessages) {
                      addMessage('unsubscribe', {
                          subscription: subscriptions.length === 1 ? subscriptions[0] : null,
                      });
                  }
                  const subscriptionHandles = subscriptions.map(({ handle }) => handle);
                  if (subscriptionHandles.length > 0) {
                      tracking.trackUnsubscribe(subscriptionHandles, finalSource);
                  }
                  if (onUnsubscribe !== null) {
                      onUnsubscribe();
                  }
                  return newUser;
              });
    };

    useEffect(() => {
        if (!isCheckingAuth && subscriptionAfterAuth !== null) {
            const { subscribed: newSubscribed, data, resolve } = subscriptionAfterAuth;
            updateSubscription(newSubscribed, data).then((newUser) => {
                resolve(newUser);
                return newUser;
            });
            setSubscriptionAfterAuth(null);
        }
    }, [isCheckingAuth, subscriptionAfterAuth, updateSubscription]);

    const subscribe = (data = null) => updateSubscription(true, data);
    const unsubscribe = (data = null) => updateSubscription(false, data);

    const updateVia = (newVia) => {
        if (newVia === 'sms' && phone === null) {
            setEditContactNeeded('phone');
            return;
        }
        if (newVia === 'email' && email === null) {
            setEditContactNeeded('email');
            return;
        }
        subscribeAsync({
            source: finalSource,
            via: newVia,
        }).then(() => {
            if (onSubscribe !== null) {
                onSubscribe();
            }
        });
    };
    const onEditContactComplete = ({ phone: newPhone, email: newEmail }) => {
        updateSubscription(true, {
            via: editContactNeeded === 'phone' ? 'sms' : 'email',
            phone: newPhone,
            email: newEmail,
            ...editContactSubscribeData.current,
        }).then((newUser) => {
            if (editContactResolve.current !== null) {
                editContactResolve.current(newUser);
            }
        });
    };

    const closeEditContact = () => {
        setEditContactNeeded(null);
        editContactResolve.current = null;
        editContactSubscribeData.current = null;
    };

    return {
        subscription,
        subscribed,
        via: userVia,
        phone,
        email,
        sourceWithPageKey: finalSource,
        updateVia,
        subscribe,
        unsubscribe,
        updateSubscription,
        isSubscribing: isSubscribing > 0,
        isUnsubscribing: isUnsubscribing > 0,
        editContactNeeded,
        onEditContactComplete,
        closeEditContact,
    };
}
